import { useEffect, useState } from 'react';
import { FiDownload, FiFileText, FiImage } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import WorkspaceDialog from './WorkspaceDialog';

const TEXT_LIMIT = 12000;

/** Queued project files can be supplied as locally persisted Blobs. Documents
 * use the existing extraction response, never a third-party viewer. */
export default function FilePreview({ file, compact = false }) {
  const { t } = useTranslation('workspace');
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [download, setDownload] = useState(null);
  const localFile = file instanceof Blob ? file : file.localFile instanceof Blob ? file.localFile : null;
  const name = file.name || t('attachmentDocument');
  const mime = file.mime_type || file.type || localFile?.type || '';
  const isImage = mime.startsWith('image/') || /\.(png|jpe?g|webp|gif|avif|bmp|heic|heif|svg)$/i.test(name);
  const isText = mime.startsWith('text/') || /\.(txt|csv)$/i.test(name);
  const extension = name.includes('.') ? name.split('.').pop().slice(0, 8).toUpperCase() : '';

  useEffect(() => {
    setPreview(null);
    if (!localFile || (!isImage && !isText)) return undefined;
    let active = true;
    const reader = new FileReader();
    setPreview({ file: localFile, loading: true });
    reader.onload = () => {
      if (active) setPreview({ file: localFile, loading: false,
        image: isImage ? String(reader.result) : '', text: isText && !isImage ? String(reader.result) : '' });
    };
    reader.onerror = () => { if (active) setPreview({ file: localFile, loading: false }); };
    // data: images are already allowed by the application's CSP.
    if (isImage) reader.readAsDataURL(localFile);
    else reader.readAsText(localFile.slice(0, TEXT_LIMIT * 4));
    return () => { active = false; if (reader.readyState === FileReader.LOADING) reader.abort(); };
  }, [localFile, isImage, isText]);

  useEffect(() => {
    if (!open || !localFile) return undefined;
    const url = URL.createObjectURL(localFile);
    setDownload({ file: localFile, url });
    return () => URL.revokeObjectURL(url);
  }, [open, localFile]);

  const current = preview?.file === localFile ? preview : null;
  const text = [file.extracted_text, file.text, current?.text, file.preview]
    .find((value) => typeof value === 'string' && value.trim()) || '';
  const image = current?.image;
  const loading = current?.loading || (!isImage && !text && file.status === 'uploading');
  const kind = isImage ? t('attachmentImage') : extension || t('attachmentDocument');
  const imageError = () => setPreview((previous) => previous?.file === localFile ? { ...previous, image: '' } : previous);

  return <>
    <button type="button" className={`ws-file-preview-button${compact ? ' ws-file-preview-compact' : ''}`}
      onClick={() => setOpen(true)} title={t('attachmentPreview', { name })}
      aria-label={t('attachmentPreview', { name })} aria-haspopup="dialog">
      <span className={`ws-file-thumbnail${isImage ? ' ws-file-thumbnail-image' : ''}`} aria-hidden="true">
        {image ? <img src={image} alt="" onError={imageError} /> : isImage ? <FiImage /> : <>
          <FiFileText /><span className="ws-file-extension">{extension}</span>
          {text && <span className="ws-file-thumbnail-text">{text.slice(0, 160)}</span>}
        </>}
      </span>
      {!compact && <span className="ws-file-preview-caption"><strong>{name}</strong><small>{file.status === 'uploading' ? t('uploading') : kind}</small></span>}
    </button>
    {open && <WorkspaceDialog title={name} onClose={() => setOpen(false)} className="ws-file-preview-dialog">
      <div className="ws-file-preview-body">
        {image ? <img className="ws-file-preview-image" src={image} alt={name} onError={imageError} />
          : loading ? <p role="status" className="ws-muted">{t('attachmentPreviewLoading')}</p>
          : !isImage && text ? <><p className="ws-file-preview-label">{t('attachmentExcerpt')}</p><pre className="ws-file-preview-text">{text.slice(0, TEXT_LIMIT)}</pre></>
          : <p className="ws-muted">{t('attachmentPreviewUnavailable')}</p>}
        {localFile && download?.file === localFile && <a className="ws-secondary ws-file-preview-download" href={download.url} download={name}><FiDownload aria-hidden="true" />{t('attachmentDownload')}</a>}
      </div>
    </WorkspaceDialog>}
  </>;
}
